import { z } from 'zod';
import config from './config';

// Custom phone number validation that matches the existing format
const phoneValidation = z
  .string()
  .min(10, 'Invalid phone number')
  .max(15, 'Phone number must be at most 15 digits')
  .regex(/^[\d\s\-\(\)\+]+$/, 'Phone number can only contain digits, spaces, dashes, parentheses, and plus sign')
  .refine((phone) => {
    // Remove all non-digit characters to count actual digits
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }, 'Phone number must contain 10-15 digits');

// Contact Form Validation Schema
export const contactFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Name is too short')
    .max(50, 'Name must be at most 50 characters')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),

  email: z
    .string()
    .email('Please enter a valid email address')
    .min(5, 'Email must be at least 5 characters')
    .max(100, 'Email must be at most 100 characters'),

  phone: phoneValidation,

  subject: z.enum(
    [
      'general',
      'catering',
      'order-issue',
      'menu-request',
      'event-booking',
      'bulk-order',
      'feedback',
      'partnership',
      'career',
      'other',
    ],
    {
      message: 'Please select a valid subject',
    },
  ),

  message: z
    .string()
    .min(10, 'Message is too short (10 characters min)')
    .max(750, 'Message is too long (750 characters max)'),
});

// Delivery Information Validation Schema
export const deliveryFormSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Full name is too short')
    .max(100, 'Full name is too long (100 characters max)')
    .regex(/^[a-zA-Z\s\-'\.]+$/, 'Name can only contain letters, spaces, hyphens, apostrophes, and periods'),

  phoneNumber: phoneValidation,

  email: z
    .string()
    .email('Please enter a valid email address')
    .min(5, 'Email must be at least 5 characters')
    .max(100, 'Email must be at most 100 characters'),

  contactMethod: z.enum(['email', 'phone'], {
    message: 'Please select a contact method',
  }),

  streetAddress: z
    .string()
    .min(10, 'Valid street address is required')
    .max(200, 'Street address is too long (200 characters max)'),

  city: z
    .string()
    .min(2, 'Please enter a city')
    .max(50, 'City name is invalid')
    .regex(/^[a-zA-Z\s\-'\.]+$/, 'City can only contain letters, spaces, hyphens, apostrophes, and periods'),

  state: z.enum(
    config.allowedStates.map((state) => state.ab),
    {
      message: 'Please select a valid state',
    },
  ),

  zipCode: z
    .string()
    .min(5, 'Please enter a valid ZIP code')
    .max(5, 'ZIP code is invalid')
    .regex(/^[\d\-\s]+$/, 'ZIP code can only contain digits, dashes, and spaces'),

  deliveryDate: z.number().refine((date) => {
    return date > Date.now();
  }, 'Delivery date must be in the future'),

  deliveryTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9] [AP]M$/, 'Please select a valid delivery time'),
});

// Login Form Validation Schema
export const loginFormSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .min(5, 'Email must be at least 5 characters')
    .max(100, 'Email must be at most 100 characters'),

  password: z.string().min(1, 'Password cannot be empty').max(100, 'Password must be at most 100 characters'),
});

// Dietary Requirements Validation Schema
export const dietaryFormSchema = z.object({
  vegetarian: z.boolean().optional(),
  vegan: z.boolean().optional(),
  glutenFree: z.boolean().optional(),
  dairyFree: z.boolean().optional(),
  nuts: z.boolean().optional(),
  shellfish: z.boolean().optional(),
  eggs: z.boolean().optional(),
  soy: z.boolean().optional(),
  additionalComments: z
    .string()
    .max(300, 'Additional comments must be at most 300 characters')
    .optional()
    .or(z.literal('')),
});

// Export types for TypeScript
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type DeliveryFormData = z.infer<typeof deliveryFormSchema>;
export type LoginFormData = z.infer<typeof loginFormSchema>;
export type DietaryFormData = z.infer<typeof dietaryFormSchema>;
